<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      {
        expanded:
          !isVerticalMenuCollapsed ||
          (isVerticalMenuCollapsed && isMouseHovered),
      },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- Main Menu Header -->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">
          <!-- Logo -->
          <li class="nav-item flex-fill text-center">
            <b-link class="navbar-brand text-center d-block" to="/dashboard">
              <b-img class="mb-1" :src="appLogoImage" alt="logo" style="width: 200px; height: auto;" />
            </b-link>
          </li>
        </ul>
      </slot>
    </div>
    <!-- / Main Menu Header -->

    <!-- Main Menu Content -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      tagname="div"
      @ps-scroll-y="handleScroll"
    >
      <vertical-nav-menu-items
        :items="navMenuItems"
        class="navigation navigation-main"
      />
    </vue-perfect-scrollbar>
    <!-- / Main Menu Content -->
  </div>
</template>


<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { BLink, BImg } from "bootstrap-vue";
import { provide, computed, ref } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { $themeConfig } from "@themeConfig";
import VerticalNavMenuItems from "./components/vertical-nav-menu-items/VerticalNavMenuItems.vue";
import useVerticalNavMenu from "./useVerticalNavMenu";
import store from "@/store";

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const navMenuItems = store.getters["appData/getNavMenuItems"];

    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props);

    const { skin } = useAppConfig();

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false);

    provide("isMouseHovered", isMouseHovered);

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const collapseTogglerIconFeather = computed(() =>
      collapseTogglerIcon.value === "unpinned" ? "CircleIcon" : "DiscIcon"
    );

    // App Name
    const { appName, appLogoImage } = $themeConfig.app;

    const handleScroll = (evt) => {
      shallShadowBottom.value = evt.srcElement.scrollTop > 0;
    };

    return {
      navMenuItems,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,

      // Scroll Handler
      handleScroll,
    };
  },
};
</script>


<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu {
  display: flex;
  flex-direction: column;
  height: 100vh; /* Full viewport height */
  overflow: hidden; /* Prevent overflowing content */
}

.navbar-header {
  flex: 0 0 11rem; /* Fixed height for the header */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0;
  
  .navbar-brand {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    
    img {
      transition: all 0.3s ease;
    }
  }
}

.main-menu-content {
  flex: 1 1 auto; /* Take up remaining space */
  overflow: hidden; /* Let vue-perfect-scrollbar handle scrolling */
  /* Optional: Add padding if necessary */
  padding-bottom: 2rem; /* Prevent clipping of last menu item */
}

.scroll-area {
  height: 100%; /* Ensure the scrollbar takes full height of container */
}

/* Ensure logo looks good in collapsed state */
.menu-collapsed .navbar-header {
  .navbar-brand img {
    width: 35px !important;
    height: auto !important;
  }
}
</style>
